import { PARKING_FILTER_GROUPS, type ParkingFilterGroup, type ParkingPropertyFilter } from '@repo/domain';
import { ArrowRightIcon, XIcon } from '@repo/icons';
import { tr } from '@repo/locale';
import type { TestId } from '@repo/test-id';
import { Badge, Button, BorderedCheckbox, Modal } from '@repo/ui';

import SubPropertyFilter from '@features/parkingMap/components/SubPropertyFilter';

import { parkingFilterDictionary, type ParkingFilterTopKey } from '@shared/constants/parkingFilterDictionary';
import { useParkingSearchStore, selectSelectedFiltersCount } from '@shared/stores/useParkingSearchStore';

interface Props {
  testId: TestId;
}

export default function FiltersModal({ testId }: Props) {
  const filters = useParkingSearchStore((state) => state.filters);
  const resetFilters = useParkingSearchStore((state) => state.resetFilters);
  const setFilter = useParkingSearchStore((state) => state.setFilter);
  const selectedFiltersCount = useParkingSearchStore(selectSelectedFiltersCount);
  const areFiltersOpen = useParkingSearchStore((state) => state.areFiltersOpen);
  const closeFilters = useParkingSearchStore((state) => state.closeFilters);

  const handleOpenChange = (open: boolean) => {
    if (!open) closeFilters();
  };

  const handleFilterChange = (filter: ParkingPropertyFilter, newValue: boolean): void => {
    setFilter(filter, newValue);
  };

  const handleFiltersChange = (filters: ParkingPropertyFilter[], newValue: boolean): void => {
    filters.forEach((filter) => setFilter(filter, newValue));
  };

  const isFilterGroupKey = (key: ParkingFilterTopKey): key is ParkingFilterGroup =>
    (PARKING_FILTER_GROUPS as readonly string[]).includes(key);

  const handleShowResults = (): void => {
    closeFilters();
  };

  const footer = (
    <Button
      onClick={handleShowResults}
      color="brand"
      size="lg"
      className="w-full"
      rightIcon={ArrowRightIcon}
      testId={testId.append('showResults')}
      aria-label={tr('parking_search:filter.show_results')}
    >
      {tr('parking_search:filter.show_results')}
    </Button>
  );

  return (
    <Modal
      open={areFiltersOpen}
      onOpenChange={handleOpenChange}
      flush
      dismissableOnClickOutside={false}
      testId={testId}
      title={tr('parking_search:filter.title')}
      footer={footer}
    >
      <div className="border-stroke-base mt-4 flex flex-col border-t">
        <div className="flex flex-1 flex-col gap-4 overflow-auto p-4">
          <div className="inline-flex items-center justify-between self-stretch">
            <div className="flex items-center justify-start gap-2">
              <div className="flex items-center justify-start gap-1">
                <div className="text-heading justify-start text-center text-base leading-6 font-medium">
                  {tr('parking_search:filter.selected')}
                </div>
              </div>
              <div className="flex items-center justify-start gap-1">
                <Badge
                  label={selectedFiltersCount.toString()}
                  circle
                  color="white"
                  className="h-9 w-9"
                  testId={testId.append('count')}
                />
              </div>
            </div>
            <Button
              color="dark"
              variant="outline"
              size="xs"
              label={tr('parking_search:filter.reset_filters')}
              leftIcon={XIcon}
              onClick={resetFilters}
              testId={testId.append('resetFilters')}
            />
          </div>
          <div className="flex items-center justify-center">
            <div className="grid grid-cols-3 gap-4">
              {(Object.keys(parkingFilterDictionary) as ParkingFilterTopKey[]).map((key) => {
                if (isFilterGroupKey(key)) {
                  return (
                    <SubPropertyFilter
                      key={key}
                      groupKey={key}
                      filters={filters}
                      onChange={handleFiltersChange}
                      testId={testId.append(key)}
                      label={tr(parkingFilterDictionary[key].nameTranslationKey)}
                    />
                  );
                }
                const entry = parkingFilterDictionary[key];
                return (
                  <BorderedCheckbox
                    key={key}
                    icon={entry.icon}
                    checked={filters[key]}
                    onChange={(newValue) => handleFilterChange(key, newValue)}
                    testId={testId.append(key)}
                    label={tr(entry.nameTranslationKey)}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
